import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import AuthFlowLayout from "../../layouts/AuthFlowLayout";
import IconLink from "../../components/IconLink";
import Loader from "../../components/Loader";
import ButtonContainer from "../../components/ButtonContainer";
import GradientButton from "../../components/GradientButton";
import PasswordInput from "../../components/PasswordInput";
import PrimaryColorButton from "../../components/PrimaryColorButton";
import { useAuthService } from "../../api/authService";

const ResetPassword: React.FC = () => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { passwordReset } = useAuthService();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const accessKey = searchParams.get("accessKey");
  const email = searchParams.get("email");

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one symbol"
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values) => {
      if (!accessKey || !email) {
        toast.error("Invalid reset link. Please request a new password reset.");
        return;
      }

      try {
        setIsRequesting(true);
        const response = await passwordReset({
          password: values.password,
          email: email,
          accessKey: accessKey,
        });

        if (response.success) {
          setResetSuccess(true);
          toast.success("Password reset successful");
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else {
          toast.error(response.message || "Failed to reset password");
        }
      } catch (error: any) {
        console.error("Error resetting password:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to reset password"
        );
      } finally {
        setIsRequesting(false);
      }
    },
  });

  if (!accessKey || !email) {
    return (
      <AuthFlowLayout
        headerText="Invalid Reset Link"
        subheaderText="The password reset link is invalid or has expired."
        headerContent={
          <IconLink to="/" src="/Logo-light.svg" alt="Logo" className="mb-4" />
        }
        isLoading={false}
        loader={<Loader />}
      >
        <div className="text-center">
          <p className="mb-6">Please request a new password reset.</p>
          <ButtonContainer
            firstButton={
              <GradientButton
                type="button"
                text="Back to Login"
                onClick={() => navigate("/login")}
              />
            }
          />
        </div>
      </AuthFlowLayout>
    );
  }

  return (
    <AuthFlowLayout
      headerText={
        resetSuccess ? "Password Reset Successful" : "Reset Your Password"
      }
      subheaderText={
        resetSuccess
          ? "Your password has been successfully reset."
          : "Please enter your new password below."
      }
      headerContent={
        <IconLink to="/" src="/Logo-light.svg" alt="Logo" className="mb-4" />
      }
      isLoading={isRequesting}
      loader={<Loader />}
    >
      {!resetSuccess ? (
        <form onSubmit={formik.handleSubmit}>
          <PasswordInput
            {...formik.getFieldProps("password")}
            label="New Password"
            error={formik.touched.password && formik.errors.password}
          />
          <PasswordInput
            {...formik.getFieldProps("confirmPassword")}
            label="Confirm Password"
            error={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
          />
          <ButtonContainer
            firstButton={
              <PrimaryColorButton
                type="button"
                text="Back to Login"
                onClick={() => navigate("/login")}
              />
            }
            secondButton={
              <GradientButton
                type="submit"
                text="Reset Password"
                disabled={isRequesting}
              />
            }
          />
        </form>
      ) : (
        <div className="text-center">
          <p className="mb-6">You can now log in with your new password.</p>
          <ButtonContainer
            firstButton={
              <GradientButton
                type="button"
                text="Go to Login"
                onClick={() => navigate("/login")}
              />
            }
          />
        </div>
      )}
    </AuthFlowLayout>
  );
};

export default ResetPassword;
